import React from 'react';

import _ from 'lodash';
import { connect } from 'react-redux';

import { Impression } from '@nerdwallet/analytics/react/impression';
import {
  AnalyticsPageView,
  AnalyticsProvider,
} from '@nerdwallet/seo-analytics';

import Results from '../../components/currency/results/results';
import ControlPanel from '../../components/rentvsbuy-calculator/control-panel/control-panel';
import RVBResultsPanel from '../../components/rentvsbuy-calculator/results-panel/results-panel';
import ToolLinkCards from '../../components/currency/tool-link-cards/tool-link-cards';

// types
import type {
  RentVsBuyValidations,
  RentVsBuyCalculator,
} from '../../components/rentvsbuy-calculator/types';

// Configs and Data
import {
  SECTION_NAMES,
  IMPRESSION_TRACKING_NAMES,
  SECTION_POSITION,
  RELATED_ARTICLES,
} from '../../components/rentvsbuy-calculator/analytics-constants';
import toolLinkConfig from '../../components/rentvsbuy-calculator/tool-link-config';
import { useUrlRecordContext } from '../../components/url-record';
import lazy, { Hydrate } from '../../components/lazy/lazy-universal';
import { ENTITY_NAME } from '~/client/lib/analytics-constants';

export interface RVBResultsProps {
  rentvsbuyCalculator: RentVsBuyCalculator;
  validations: RentVsBuyValidations;
}

const RelatedArticles = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-rvb-related-articles' */ '../../components/currency/related-articles/related-articles'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const Methodology = lazy(
  () =>
    import(
      /* webpackChunkName: 'lazy-methodology' */ '../../components/currency/methodology/methodology'
    ),
  { hydrate: Hydrate.WHEN_VISIBLE }
);

const RVBResults = (props: RVBResultsProps): JSX.Element => {
  const { rentvsbuyCalculator, validations } = props;

  const { title, synopsis } = useUrlRecordContext();

  const data = {
    ...rentvsbuyCalculator,
    validations,
  };

  return (
    <AnalyticsProvider>
      <AnalyticsPageView name={ENTITY_NAME.RENT_VS_BUY_CALCULATOR}>
        <Impression
          eventType="page_impression"
          entityName="mortgages_rent_vs_buy_calculator"
        >
          <Results
            {..._.assign({}, props, {
              className: 'rvb-app',
              controlPanel: (
                <ControlPanel enableTracking toggleTitle="Refine Results" />
              ),
              data,
              heading: title,
              subcopy: synopsis,
              resultsPanel: <RVBResultsPanel trackingFormName="rvb" />,
              methodology: (
                <Methodology
                  trackingFormName="methodology"
                  impressionTracking
                />
              ),
              relatedArticles: (
                <RelatedArticles
                  entityName={RELATED_ARTICLES.entityName}
                  sectionName={RELATED_ARTICLES.sectionName}
                  sectionPosition={RELATED_ARTICLES.sectionPosition}
                  articles={RELATED_ARTICLES.articles}
                />
              ),
              toolLinkCards: (
                <ToolLinkCards
                  header={toolLinkConfig.header}
                  cards={toolLinkConfig.cards}
                  impressionTrackingName={
                    IMPRESSION_TRACKING_NAMES.TOOL_LINK_CARDS
                  }
                  data={data}
                  calculatorType="rent_vs_buy_calculator"
                  analyticsConfig={{
                    sectionName: SECTION_NAMES.TOOL_LINK_CARDS,
                    sectionPosition: SECTION_POSITION.TOOL_LINK_CARDS,
                  }}
                />
              ),
            })}
          >
            <div />
          </Results>
        </Impression>
      </AnalyticsPageView>
    </AnalyticsProvider>
  );
};

RVBResults.displayName = 'RVBResults';

RVBResults.defaultProps = {
  validations: {},
};

const mapStateToProps = (state) => ({
  rentvsbuyCalculator: state?.rentvsbuyCalculator,
  validations: state?.validations,
});

export default connect(mapStateToProps)(RVBResults);
